import Analytics from '@/utils/analytics';
import countries from '@assets/countries.json';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { CONTAINER_MODAL_ROUTES } from '@/navigators/routes';
import { CountryCode } from 'libphonenumber-js';
import { CountrySelectorScreenParams } from '@/navigators/types';
import {
  DEFAULT_COUNTRY_CODE,
  formatPhoneNumber,
  getE164PhoneNumber,
} from '@/utils/phone-number';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, Text, TouchableOpacity } from 'react-native';
import { TextInput, Props as TextInputProps } from './text-input';
import { TYPOGRAPHY } from '@/styles';
import { useNavigation } from '@react-navigation/native';

const COUNTRY_SELECTOR_HIT_SLOP = {
  bottom: 12,
  left: 12,
  right: 12,
  top: 12,
};

export type Props = Omit<TextInputProps, 'value'> & {
  allowInvalidNumber?: boolean;
  initialValue?: string;
  onChangeText: (phoneNumber: string | null) => void;
};

type ControlledProps = TextInputProps & {
  onCountryCodeChange: (newCountryCode: CountryCode) => void;
  // Custom handler for opening country selector, it will skip navigation
  onOpenCountrySelector?: () => void;
  selectedCountryCode: CountryCode;
};

export const usePhoneNumberState = (
  initialValue: string,
  onChangeText?: Props['onChangeText'],
  allowInvalidNumber?: Props['allowInvalidNumber'],
) => {
  const [phoneNumber, setPhoneNumber] = useState(
    formatPhoneNumber(initialValue || ''),
  );

  const [selectedCountryCode, setSelectedCountryCode] =
    useState<CountryCode>(DEFAULT_COUNTRY_CODE);

  return {
    phoneNumber,
    selectedCountryCode,
    setPhoneNumber: (nextPhoneNumber: string) => {
      setPhoneNumber(nextPhoneNumber);
      onChangeText?.(
        getE164PhoneNumber(
          nextPhoneNumber,
          selectedCountryCode,
          allowInvalidNumber,
        ),
      );
    },
    setSelectedCountryCode: (nextCountryCode: CountryCode) => {
      setSelectedCountryCode(nextCountryCode);
      onChangeText?.(getE164PhoneNumber(phoneNumber, nextCountryCode));
    },
  };
};

type CountrySelectorRouteName = typeof CONTAINER_MODAL_ROUTES.CountrySelector;
type CountrySelectorParamsMap = {
  [K in CountrySelectorRouteName]: CountrySelectorScreenParams;
};

export const ControlledPhoneNumberInput: React.FC<ControlledProps> = ({
  value,
  selectedCountryCode,
  onCountryCodeChange,
  onOpenCountrySelector,
  onChangeText,
  label = i18n.t('phone_number'),
  editable = true,
  ...rest
}) => {
  const { navigate } =
    useNavigation<
      StackNavigationProp<CountrySelectorParamsMap, CountrySelectorRouteName>
    >();

  const selectedCountry = useMemo(
    () => countries.find((c) => c.code === selectedCountryCode)!,
    [selectedCountryCode],
  );

  const handleCountryButtonPress = () => {
    Analytics.track('phone_number_input.country_button.press');

    if (onOpenCountrySelector) {
      onOpenCountrySelector();
    } else {
      // Use standard navigation for standalone phone input usage
      navigate(CONTAINER_MODAL_ROUTES.CountrySelector, {
        initialCountryCode: selectedCountryCode,
        onCountryCodeChange,
      });
    }
  };

  return (
    <TextInput
      {...rest}
      editable={editable}
      keyboardType="phone-pad"
      label={label}
      leftElement={
        <TouchableOpacity
          accessibilityHint={i18n.t('select_a_country')}
          accessibilityLabel={i18n.t('select_a_country')}
          accessibilityRole="button"
          disabled={!editable}
          hitSlop={COUNTRY_SELECTOR_HIT_SLOP}
          onPress={handleCountryButtonPress}
          style={styles.countryButton}
          testID="country-button"
        >
          <Text style={styles.countryButtonFlag}>{selectedCountry.flag}</Text>
          <Text style={TYPOGRAPHY.p}>{selectedCountry.dialCode}</Text>
        </TouchableOpacity>
      }
      leftElementBordered
      onChangeText={onChangeText}
      textContentType="telephoneNumber"
      value={value}
    />
  );
};

export const PhoneNumberInput: React.FC<Props> = ({
  allowInvalidNumber = false,
  initialValue,
  onChangeText,
  ...rest
}) => {
  const {
    phoneNumber,
    selectedCountryCode,
    setPhoneNumber,
    setSelectedCountryCode,
  } = usePhoneNumberState(initialValue || '', onChangeText, allowInvalidNumber);

  return (
    <ControlledPhoneNumberInput
      {...rest}
      onChangeText={setPhoneNumber}
      onCountryCodeChange={setSelectedCountryCode}
      selectedCountryCode={selectedCountryCode}
      value={phoneNumber}
    />
  );
};

const styles = StyleSheet.create({
  countryButton: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  countryButtonFlag: {
    marginRight: 8,
  },
});
